import '@xyflow/react/dist/style.css';

import { useFetchInitialConfigData } from '@repo/config-services';
import { translate, TranslationEntity, useTheme } from '@repo/utils';
import {
  Background, BackgroundVariant, type Connection, type Edge, type EdgeChange,
  type EdgeTypes,
  type NodeChange, type NodeTypes, ReactFlow,
} from '@xyflow/react';
import { Flex, Result, Spin } from 'antd';
import ConditionNode from 'automations/components/BaseNodes/ConditionNode';
import NewBlockNode from 'automations/components/BaseNodes/NewBlockNode';
import TriggerNode from 'automations/components/BaseNodes/TriggerNode';
import { EdgeType } from 'automations/data/edgeTypes';
import { NodeType } from 'automations/data/nodeTypes';
import withAutomationProvider from 'automations/hoc/withAutomationProvider';
import { useAppSelector } from 'automations/hooks/useAppSelector';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useNodesInjector from 'automations/hooks/useNodesInjector';
import useReportUnsavedChanges from 'automations/hooks/useReportUnsavedChanges';
import { edgesSelector, nodesSelector } from 'automations/selectors/reactFlow';
import {
  resetReducer, setAutomationId, setAutomationMode, setDebug,
  setReportExecutionId,
} from 'automations/store/automationConfigSlice';
import {
  deleteAllNodes, onConnect, onEdgesChange, onNodesChange, setHoveredEdgeId,
  updateInitialNodePosition,
} from 'automations/store/reactFlowSlice';
import { AutomationMode } from 'automations/types/automation';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import AutomationCommonReportPanel from '../AutomationCommonReportPanel';
import AutomationHeader from '../AutomationHeader';
import DefaultEdge from '../BaseEdges/DefaultEdge';
import ElseEdge from '../BaseEdges/ElseEdge';
import IfEdge from '../BaseEdges/IfEdge';
import ActionNode from '../BaseNodes/ActionNode';
import FallbackNode from '../BaseNodes/FallbackNode';
import ReactFlowUtil from '../ReactFlowUtil';
import styles from './AutomationCanvas.module.scss';
import CanvasAlert from './CanvasAlert';
import ReactFlowControls from './ReactFlowControls';

export const NodeMap: NodeTypes = {
  [NodeType.newBlock]: NewBlockNode,
  [NodeType.trigger]: TriggerNode,
  [NodeType.condition]: ConditionNode,
  [NodeType.action]: ActionNode,
  [NodeType.fallback]: FallbackNode,
};

export const EdgeMap: EdgeTypes = {
  [EdgeType.default]: DefaultEdge,
  [EdgeType.if]: IfEdge,
  [EdgeType.else]: ElseEdge,
};

function AutomationCanvasContent() {
  const { theme } = useTheme();
  const nodes = useAppSelector(nodesSelector);
  const edges = useAppSelector(edgesSelector);
  const dispatch = useAutomationDispatch();
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const { isFetching, isError } = useNodesInjector();
  useReportUnsavedChanges();

  useEffect(() => {
    const updateSize = () => {
      setCanvasSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', updateSize);
    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    if (canvasSize.width > 0 && canvasSize.height > 0) {
      dispatch(updateInitialNodePosition({
        x: canvasSize.width / 2 - 100,
        y: 50, // 50px from the top
      }));
    }
  }, [canvasSize, dispatch]);

  const handleNodesChange = useCallback((args: NodeChange[]) => {
    dispatch(onNodesChange(args));
  }, [dispatch]);

  const handleEdgesChange = useCallback((args: EdgeChange[]) => {
    dispatch(onEdgesChange(args));
  }, [dispatch]);

  const handleConnect = useCallback((args: Connection) => {
    dispatch(onConnect(args));
  }, [dispatch]);

  const proOptions = {
    hideAttribution: true,
  };

  const edgeOptions = useMemo(() => ({
    type: 'smoothstep',
  }), []);

  const handleEdgeMouseEnter = useCallback((_: any, edge: Edge) => {
    dispatch(setHoveredEdgeId(edge.id));
  }, [dispatch]);

  const handleEdgeMouseLeave = useCallback(() => {
    dispatch(setHoveredEdgeId(null));
  }, [dispatch]);

  return (
    <Flex vertical className={styles.canvasContainer}>
      <AutomationHeader />
      {isFetching && <Spin fullscreen spinning tip={translate(`Loading #{${TranslationEntity.automation}}...`)} />}
      {isError ? (
        <Result
          status="error"
          title={translate(`Failed to load #{${TranslationEntity.automation}}`)}
          subTitle="Please try refreshing the page, If the issue persists, please contact support."
        />
      ) : (
        <ReactFlow
          colorMode={theme}
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={handleConnect}
          proOptions={proOptions}
          defaultEdgeOptions={edgeOptions}
          nodesDraggable={false}
          nodesConnectable={false}
          nodeTypes={NodeMap}
          edgeTypes={EdgeMap}
          fitView
          onEdgeMouseEnter={handleEdgeMouseEnter}
          onEdgeMouseLeave={handleEdgeMouseLeave}
        >
          <CanvasAlert />
          <ReactFlowControls />
          <Background
            variant={BackgroundVariant.Dots}
            gap={30}
            size={1}
          />
          <AutomationCommonReportPanel />
        </ReactFlow>
      )}
      <ReactFlowUtil />
    </Flex>
  );
}

function AutomationCanvas({
  automationId,
  reportMode,
  liveEdit,
  reportExecutionId,
  debug,
}: {
  automationId: string;
  reportMode?: boolean;
  liveEdit?: boolean;
  reportExecutionId?: string;
  debug?: boolean;
}) {
  useFetchInitialConfigData();
  const dispatch = useAutomationDispatch();

  const selectedAutomationId = useAutomationId();

  useEffect(() => {
    if (selectedAutomationId !== automationId) {
      dispatch(deleteAllNodes());
      dispatch(resetReducer());
    }
    dispatch(setAutomationId(automationId));
    dispatch(setDebug(debug ?? false));

    let mode: AutomationMode = AutomationMode.default;
    if (liveEdit) mode = AutomationMode.liveEdit;
    if (reportMode) {
      if (reportExecutionId) {
        mode = AutomationMode.individualReport;
        dispatch(setReportExecutionId(reportExecutionId));
      } else {
        mode = AutomationMode.commonReport;
      }
    }
    dispatch(setAutomationMode(mode));
  }, [automationId, dispatch, liveEdit, reportExecutionId, reportMode, debug, selectedAutomationId]);

  return <AutomationCanvasContent />;
}

export default withAutomationProvider(AutomationCanvas);
